import { useEffect, useRef, useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import { ArrowBack, Close, Add, Delete, Edit, Send, ThumbUp, ThumbDown, Widgets } from "@mui/icons-material";
import dayjs from "dayjs";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ConfirmModal } from "@/components/ConfirmModal";
import type { ChatConversation, ChatMessage } from "@/api/chat.service";
import {
  createConversation,
  deleteConversation,
  listConversations,
  listMessages,
  markConversationAsRead,
  sendMessage,
  setReaction,
  updateConversation,
} from "@/api/chat.service";
import { getAllModules, type ManifestModule } from "@/api/modules.service";
import { getModulesByUserAndClient } from "@/api/user.service";
import { whatsappToMarkdown } from "@/utils/format";

function formatDate(iso: string): string {
  const date = dayjs(iso);
  const now = dayjs();
  const diffDays = now.diff(date, "day");
  if (diffDays === 0) return date.format("HH:mm");
  if (diffDays === 1) return "Ontem";
  if (diffDays < 7) return date.format("ddd");
  return date.format("DD/MM");
}

interface Props {
  open: boolean;
  onClose: () => void;
  clientId: string;
  userId: string;
  // ponytail: se ausente, modo "global" — lista conversas de todos os módulos e usa
  // o ModulePicker antes de abrir uma conversa nova (igual ao FAB de home.tsx/
  // listaChat.tsx no mobile). Se presente (uso atual da FlashPage), comportamento
  // igual a antes: escopado a um módulo só, sem picker.
  moduleId?: string;
}

export function ChatDrawer({ open, onClose, clientId, userId, moduleId }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeTopic, setActiveTopic] = useState<string | null>(null);
  const [activeModuleId, setActiveModuleId] = useState<string | null>(moduleId ?? null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const reset = () => {
    setActiveId(null);
    setActiveTopic(null);
    setActiveModuleId(moduleId ?? null);
    setPickerOpen(false);
  };

  const handleNew = () => {
    if (moduleId) {
      setActiveModuleId(moduleId);
      setActiveTopic(null);
      setActiveId("new");
    } else {
      setPickerOpen(true);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        onClose();
        reset();
      }}
      slotProps={{ paper: { sx: { width: { xs: "100%", sm: 420 }, display: "flex", flexDirection: "column" } } }}
    >
      {activeId ? (
        <ChatThread
          conversationId={activeId}
          topic={activeTopic}
          clientId={clientId}
          moduleId={activeModuleId ?? ""}
          onBack={reset}
          onDeleted={reset}
          onTopicChange={setActiveTopic}
        />
      ) : pickerOpen ? (
        <ModulePicker
          userId={userId}
          clientId={clientId}
          onBack={() => setPickerOpen(false)}
          onSelect={(module) => {
            setPickerOpen(false);
            setActiveModuleId(module.id);
            setActiveTopic(module.label);
            setActiveId("new");
          }}
        />
      ) : (
        <ConversationList
          clientId={clientId}
          userId={userId}
          moduleId={moduleId}
          onClose={onClose}
          onNew={handleNew}
          onOpenConversation={(id, topic, modId) => {
            setActiveId(id);
            setActiveTopic(topic);
            setActiveModuleId(modId);
          }}
        />
      )}
    </Drawer>
  );
}

// ── Conversation list ───────────────────────────────────────────────────────

function ConversationList({
  clientId,
  userId,
  moduleId,
  onClose,
  onNew,
  onOpenConversation,
}: {
  clientId: string;
  userId: string;
  moduleId?: string;
  onClose: () => void;
  onNew: () => void;
  onOpenConversation: (id: string, topic: string | null, moduleId: string) => void;
}) {
  const isGlobal = !moduleId;
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [modules, setModules] = useState<ManifestModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchList = (cursor?: string) => {
    listConversations(moduleId, clientId, cursor)
      .then((data) => {
        setConversations((prev) => (cursor ? [...prev, ...data.conversations] : data.conversations));
        setNextCursor(data.next_cursor);
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    if (!isGlobal) return;
    Promise.all([getAllModules(), getModulesByUserAndClient(userId, clientId)]).then(([all, allowed]) => {
      const allowedIds = new Set(allowed.map((m) => m.module_id));
      setModules(all.filter((m) => allowedIds.has(m.id)));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobal, userId, clientId]);

  useEffect(() => {
    setLoading(true);
    fetchList();
    // ponytail: poll em vez de websocket — igual ao mobile (listaChat.tsx), suficiente
    // pro volume de conversas de um painel de suporte.
    const poll = setInterval(() => fetchList(), 3000);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, moduleId]);

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2, borderBottom: "1px solid #eee" }}>
        <Typography variant="h6">Conversas</Typography>
        <Stack direction="row" gap={1}>
          <IconButton size="small" onClick={onNew} title="Nova conversa">
            <Add fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : conversations.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 6 }}>
            Nenhuma conversa.
          </Typography>
        ) : (
          <List disablePadding>
            {conversations.map((c) => {
              const moduleLabel = isGlobal ? modules.find((m) => m.id === c.module_id)?.label : undefined;
              const primary = moduleLabel ? `${moduleLabel} - ${c.topic || "nova conversa"}` : c.topic || "Nova conversa";
              return (
                <ListItemButton
                  key={c.id}
                  onClick={() => onOpenConversation(c.id, c.topic, c.module_id)}
                  sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                >
                  <ListItemText
                    primary={primary}
                    secondary={formatDate(c.created_at)}
                    primaryTypographyProps={{ fontWeight: c.unread ? 700 : 500 }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
        {nextCursor && !loading && (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <Button
              size="small"
              disabled={loadingMore}
              onClick={() => {
                setLoadingMore(true);
                fetchList(nextCursor);
              }}
            >
              Carregar mais
            </Button>
          </Stack>
        )}
      </Box>
    </>
  );
}

// ── Module picker (nova conversa em modo global) ────────────────────────────

function ModulePicker({
  userId,
  clientId,
  onBack,
  onSelect,
}: {
  userId: string;
  clientId: string;
  onBack: () => void;
  onSelect: (module: ManifestModule) => void;
}) {
  const [modules, setModules] = useState<ManifestModule[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getAllModules(), getModulesByUserAndClient(userId, clientId)])
      .then(([all, allowed]) => {
        const allowedIds = new Set(allowed.map((m) => m.module_id));
        setModules(
          all
            .filter((m) => allowedIds.has(m.id))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.label.localeCompare(b.label)),
        );
      })
      .finally(() => setLoading(false));
  }, [userId, clientId]);

  return (
    <>
      <Stack direction="row" alignItems="center" gap={1} sx={{ p: 2, borderBottom: "1px solid #eee" }}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography variant="h6">Novo chat</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5 }}>
        Selecione o módulo
      </Typography>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : modules.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 6 }}>
            Nenhum módulo disponível.
          </Typography>
        ) : (
          <List disablePadding>
            {modules.map((module) => (
              <ListItemButton key={module.id} onClick={() => onSelect(module)} sx={{ borderBottom: "1px solid", borderColor: "divider", gap: 1.5 }}>
                <Widgets fontSize="small" color="primary" />
                <ListItemText primary={module.label} secondary={module.description} />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </>
  );
}

// ── Thread ───────────────────────────────────────────────────────────────────

function ChatThread({
  conversationId,
  topic,
  clientId,
  moduleId,
  onBack,
  onDeleted,
  onTopicChange,
}: {
  conversationId: string;
  topic: string | null;
  clientId: string;
  moduleId: string;
  onBack: () => void;
  onDeleted: () => void;
  onTopicChange: (topic: string) => void;
}) {
  const isNew = conversationId === "new";
  const [convId, setConvId] = useState<string | null>(isNew ? null : conversationId);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameText, setRenameText] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loadingOlder, setLoadingOlder] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const autoScrollRef = useRef(true);
  const loadingOlderRef = useRef(false);

  useEffect(() => {
    if (isNew) return;
    // ponytail: backend devolve mais recente primeiro (ORDER BY id DESC, ver
    // db/queries/chat.sql#ListChatMessagesBefore) — precisa inverter pro layout
    // desktop, que é uma lista simples de cima pra baixo (sem a inversão implícita
    // do GiftedChat que o mobile usa).
    listMessages(conversationId)
      .then((data) => {
        setMessages([...data.messages].reverse());
        setNextCursor(data.next_cursor);
      })
      .finally(() => setLoading(false));
    markConversationAsRead(conversationId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    if (autoScrollRef.current) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    autoScrollRef.current = true;
  }, [messages]);

  const loadOlder = async () => {
    if (!nextCursor || loadingOlderRef.current) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    const container = containerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;
    try {
      const data = await listMessages(conversationId, nextCursor);
      autoScrollRef.current = false;
      setMessages((prev) => [...data.messages.slice().reverse(), ...prev]);
      setNextCursor(data.next_cursor);
      requestAnimationFrame(() => {
        if (container) container.scrollTop = container.scrollHeight - prevScrollHeight;
      });
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  };

  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const container = containerRef.current;
    if (!sentinel || !container || !nextCursor) return;
    const observer = new IntersectionObserver((entries) => entries[0].isIntersecting && loadOlder(), {
      root: container,
      threshold: 1,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextCursor]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setIsTyping(false);
  };

  const startPolling = (cId: string) => {
    stopPolling();
    setIsTyping(true);
    pollRef.current = setInterval(() => {
      listMessages(cId).then((data) => {
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const fresh = [...data.messages].reverse().filter((m) => !existingIds.has(m.id));
          if (fresh.length === 0) return prev;
          if (fresh.some((m) => m.sender === "chatbot")) stopPolling();
          return [...prev, ...fresh];
        });
      });
    }, 3000);
  };

  const handleSend = async () => {
    const content = input.trim();
    if (!content || sending) return;
    setSending(true);
    setInput("");
    const tempId = `temp-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: tempId, conversation_id: convId ?? "", sender: "user", type: "text", content, reaction: null, created_at: new Date().toISOString() },
    ]);
    try {
      let cId = convId;
      if (!cId) {
        const { conversation, message } = await createConversation(clientId, moduleId, content);
        cId = conversation.id;
        setConvId(cId);
        // ponytail: igual ao mobile (chat.tsx#onSend) — backend não recebe topic na criação,
        // então conversation.topic volta null; mantém o topic local (label do módulo já
        // escolhido) em vez de sobrescrever com "Nova conversa".
        if (conversation.topic) onTopicChange(conversation.topic);
        setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, id: message.id, conversation_id: cId! } : m)));
      } else {
        const message = await sendMessage(cId, content);
        setMessages((prev) => prev.map((m) => (m.id === tempId ? message : m)));
      }
      startPolling(cId);
    } catch {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    } finally {
      setSending(false);
    }
  };

  const handleReaction = (messageId: string, reaction: "like" | "dislike") => {
    if (!convId) return;
    const current = messages.find((m) => m.id === messageId)?.reaction ?? null;
    const next = current === reaction ? null : reaction;
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, reaction: next } : m)));
    setReaction(convId, messageId, next).catch(() => {
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, reaction: current } : m)));
    });
  };

  const handleSaveRename = async () => {
    const newTopic = renameText.trim();
    if (!newTopic || !convId) return;
    await updateConversation(convId, newTopic);
    onTopicChange(newTopic);
    setIsRenaming(false);
  };

  const handleDelete = async () => {
    if (convId) await deleteConversation(convId).catch(() => {});
    setConfirmDelete(false);
    onDeleted();
  };

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2, borderBottom: "1px solid #eee", gap: 1 }}>
        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
          <IconButton size="small" onClick={onBack}>
            <ArrowBack fontSize="small" />
          </IconButton>
          {isRenaming ? (
            <TextField
              size="small"
              autoFocus
              value={renameText}
              onChange={(e) => setRenameText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSaveRename()}
              onBlur={handleSaveRename}
              sx={{ flex: 1 }}
            />
          ) : (
            <Typography variant="subtitle1" noWrap sx={{ flex: 1 }}>
              {topic || "Nova conversa"}
            </Typography>
          )}
        </Stack>
        {convId && !isRenaming && (
          <Stack direction="row" gap={0.5}>
            <IconButton
              size="small"
              onClick={() => {
                setRenameText(topic || "");
                setIsRenaming(true);
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setConfirmDelete(true)}>
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Box ref={containerRef} sx={{ flex: 1, overflowY: "auto", p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <>
            {nextCursor && (
              <Stack ref={topSentinelRef} alignItems="center" sx={{ py: 1 }}>
                {loadingOlder && <CircularProgress size={20} />}
              </Stack>
            )}
            {messages.map((m) => (
              <Box key={m.id} sx={{ alignSelf: m.sender === "user" ? "flex-end" : "flex-start", maxWidth: "85%" }}>
                <Box
                  sx={{
                    bgcolor: m.sender === "user" ? "primary.main" : "background.subtle",
                    color: m.sender === "user" ? "primary.contrastText" : "text.primary",
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    "& > :first-of-type": { mt: 0 },
                    "& > :last-child": { mb: 0 },
                    "& pre": { bgcolor: "rgba(0,0,0,0.08)", p: 1, borderRadius: 1, overflowX: "auto" },
                    "& code": { fontFamily: "monospace", fontSize: "0.9em", bgcolor: "rgba(0,0,0,0.08)", px: 0.5, borderRadius: 0.5 },
                    "& pre code": { bgcolor: "transparent", p: 0 },
                    "& ul, & ol": { pl: 2.5 },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{whatsappToMarkdown(m.content)}</ReactMarkdown>
                </Box>
                {m.sender === "chatbot" && convId && (
                  <Stack direction="row" gap={0.5} sx={{ mt: 0.5 }}>
                    <IconButton size="small" onClick={() => handleReaction(m.id, "like")}>
                      <ThumbUp fontSize="inherit" color={m.reaction === "like" ? "primary" : "disabled"} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleReaction(m.id, "dislike")}>
                      <ThumbDown fontSize="inherit" color={m.reaction === "dislike" ? "error" : "disabled"} />
                    </IconButton>
                  </Stack>
                )}
              </Box>
            ))}
          </>
        )}
        {isTyping && (
          <Typography variant="body2" color="text.secondary">
            Digitando…
          </Typography>
        )}
        <div ref={bottomRef} />
      </Box>

      <Stack direction="row" gap={1} sx={{ p: 1.5, borderTop: "1px solid #eee" }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder="Escreva uma mensagem..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <IconButton color="primary" disabled={!input.trim() || sending} onClick={handleSend}>
          <Send />
        </IconButton>
      </Stack>

      <ConfirmModal
        open={confirmDelete}
        title="Excluir conversa"
        text="Tem certeza que deseja excluir esta conversa?"
        btnText="Excluir"
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
    </>
  );
}
